import React, { useState } from 'react';
import { Calculator, DollarSign, TrendingUp, Users, Fingerprint, FileText, Camera, Car, Award } from 'lucide-react';

const ProfitCalculatorPage = () => {
  // Calculator inputs
  const [rollingFee, setRollingFee] = useState(35);
  const [scansPerDay, setScansPerDay] = useState(10);
  const [daysPerMonth, setDaysPerMonth] = useState(22);
  const [subscriptionTier, setSubscriptionTier] = useState('professional');
  
  // Add-on services
  const [inkCards, setInkCards] = useState(20);
  const [passportPhotos, setPassportPhotos] = useState(15);
  const [mobileAppointments, setMobileAppointments] = useState(5);
  const [notaryServices, setNotaryServices] = useState(10);

  // Pricing
  const subscriptionCosts = {
    basic: 49,
    professional: 99,
    enterprise: 299,
    standalone: 0 // One-time purchase
  };

  const addOnProfits = {
    inkCard: 40,
    passportPhoto: 14.50,
    mobileAppointment: 100,
    notary: 15
  };

  // Calculations
  const monthlyScans = scansPerDay * daysPerMonth;
  const scanRevenue = monthlyScans * rollingFee;
  const inkCardRevenue = inkCards * addOnProfits.inkCard;
  const photoRevenue = passportPhotos * addOnProfits.passportPhoto;
  const mobileRevenue = mobileAppointments * addOnProfits.mobileAppointment;
  const notaryRevenue = notaryServices * addOnProfits.notary;
  
  const totalRevenue = scanRevenue + inkCardRevenue + photoRevenue + mobileRevenue + notaryRevenue;
  const subscriptionCost = subscriptionCosts[subscriptionTier];
  const monthlyProfit = totalRevenue - subscriptionCost;
  const yearlyProfit = (monthlyProfit * 12);
  const roi = subscriptionCost > 0 ? ((monthlyProfit / subscriptionCost) * 100).toFixed(0) : '∞';

  return (
    <div className="min-h-screen bg-slate-900 relative">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/90" />

      {/* Content */}
      <div className="relative z-10 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-2xl mb-4">
              <Calculator className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Vendor Profit Calculator</h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              See how much you can earn with FingerprintVS. Adjust the numbers to match your business.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* Input Section */}
            <div className="space-y-6">
              
              {/* Live Scan Settings */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Fingerprint className="w-5 h-5 text-cyan-400" />
                  Live Scan Settings
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Rolling Fee (per scan): <span className="text-green-400 font-bold">${rollingFee}</span>
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="50"
                      value={rollingFee}
                      onChange={(e) => setRollingFee(Number(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>$20</span>
                      <span>$50</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Scans Per Day: <span className="text-green-400 font-bold">{scansPerDay}</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={scansPerDay}
                      onChange={(e) => setScansPerDay(Number(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1</span>
                      <span>50</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Working Days Per Month: <span className="text-green-400 font-bold">{daysPerMonth}</span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="30"
                      value={daysPerMonth}
                      onChange={(e) => setDaysPerMonth(Number(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>10 days</span>
                      <span>30 days</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Tier */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-400" />
                  Your FingerprintVS Plan
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'basic', name: 'Basic', price: 49, desc: 'USB/Offline' },
                    { id: 'professional', name: 'Professional', price: 99, desc: 'Cloud' },
                    { id: 'enterprise', name: 'Enterprise', price: 299, desc: 'LAN' },
                    { id: 'standalone', name: 'Standalone', price: 0, desc: 'One-time $499' },
                  ].map((tier) => (
                    <button
                      key={tier.id}
                      onClick={() => setSubscriptionTier(tier.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        subscriptionTier === tier.id
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="text-white font-semibold">{tier.name}</div>
                      <div className="text-xs text-gray-400">{tier.desc}</div>
                      <div className="text-green-400 font-bold mt-1">
                        {tier.price > 0 ? `$${tier.price}/mo` : 'No monthly fee'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-On Services */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-400" />
                  Add-On Services (per month)
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Ink Cards ({inkCards})
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="200"
                      value={inkCards}
                      onChange={(e) => setInkCards(Number(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                    <div className="text-xs text-gray-500 mt-1">$40 profit each</div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      <Camera className="w-4 h-4 inline mr-1" />
                      Passport Photos ({passportPhotos})
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="200"
                      value={passportPhotos}
                      onChange={(e) => setPassportPhotos(Number(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                    <div className="text-xs text-gray-500 mt-1">$14.50 profit each</div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      <Car className="w-4 h-4 inline mr-1" />
                      Mobile Visits ({mobileAppointments})
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={mobileAppointments}
                      onChange={(e) => setMobileAppointments(Number(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                    <div className="text-xs text-gray-500 mt-1">$100 profit each</div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Notary Services ({notaryServices})
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={notaryServices}
                      onChange={(e) => setNotaryServices(Number(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                    <div className="text-xs text-gray-500 mt-1">$15 profit each</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              
              {/* Main Results */}
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30">
                <h3 className="text-lg font-semibold text-white mb-6 text-center">Your Estimated Earnings</h3>
                
                <div className="text-center mb-8">
                  <div className="text-6xl font-bold text-green-400 mb-2">
                    ${monthlyProfit.toLocaleString()}
                  </div>
                  <div className="text-gray-400">Monthly Profit</div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-black/20 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-white">${yearlyProfit.toLocaleString()}</div>
                    <div className="text-sm text-gray-400">Yearly Profit</div>
                  </div>
                  <div className="bg-black/20 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-cyan-400">{roi}%</div>
                    <div className="text-sm text-gray-400">ROI on Subscription</div>
                  </div>
                </div>

                <div className="text-center">
                  <a 
                    href="/pricing"
                    className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
                  >
                    <TrendingUp className="w-5 h-5" />
                    Start Earning Today
                  </a>
                </div>
              </div>

              {/* Breakdown */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4">Monthly Breakdown</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-gray-400">Live Scans ({monthlyScans} × ${rollingFee})</span>
                    <span className="text-green-400 font-semibold">+${scanRevenue.toLocaleString()}</span>
                  </div>
                  
                  {inkCards > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-gray-400">Ink Cards ({inkCards} × $40)</span>
                      <span className="text-green-400 font-semibold">+${inkCardRevenue.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {passportPhotos > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-gray-400">Passport Photos ({passportPhotos} × $14.50)</span>
                      <span className="text-green-400 font-semibold">+${photoRevenue.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {mobileAppointments > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-gray-400">Mobile Visits ({mobileAppointments} × $100)</span>
                      <span className="text-green-400 font-semibold">+${mobileRevenue.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {notaryServices > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-gray-400">Notary Services ({notaryServices} × $15)</span>
                      <span className="text-green-400 font-semibold">+${notaryRevenue.toLocaleString()}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-gray-400">Total Revenue</span>
                    <span className="text-white font-semibold">${totalRevenue.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-gray-400">FingerprintVS Subscription</span>
                    <span className="text-red-400 font-semibold">-${subscriptionCost}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 bg-green-500/10 rounded-lg px-3">
                    <span className="text-white font-semibold">Net Monthly Profit</span>
                    <span className="text-green-400 font-bold text-xl">${monthlyProfit.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Government Fees Note */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <p className="text-amber-200 text-sm">
                  <strong>Note:</strong> Government fees ($17-$75) are collected from clients and passed through to DOJ/FBI. 
                  They don't affect your profit — your rolling fee is pure revenue.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitCalculatorPage;
